import React, { useEffect, useState, useRef, useContext } from 'react'
import { IColumnTypes, IColumnValuesCount } from 'components/spreadsheet/types'
import { getRowHeightVariable, resolveDependencyCachedOptions } from 'components/spreadsheet/helpers/functions'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { ICellValue, IContextMenuState, ITableRow, ITableViewColour, ITableViewColumn, IRowLevelColour } from 'types'
import { DataContext } from 'components/spreadsheet/contexts/data/DataContext'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { ProjectContext } from 'hooks/projectContext'
import { ApplicationContext } from 'hooks/application'
import { getCellColour } from 'components/spreadsheet/helpers/colouring'
import Button from 'components/button'
import { Triangle } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import SelectMenu from 'components/select/components/menu'
import { checkValuesSame } from 'components/spreadsheet/helpers/paste'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'
import { useThemedColours } from 'hooks/useTheme'

interface SelectCellProps {
  value: string | string[]
  align: 'center' | 'left' | 'right'
  color: string
  kind: IColumnTypes
  readOnly: boolean
  isAdmin: boolean
  isContributor: boolean
  rowHeight: number
  selected: boolean
  isExpanded?: boolean
  validation: boolean
  isJoined: boolean
  editing?: boolean
  onCloseSelect?: () => void
  formValues?: Record<string, ICellValue>
  handleKeyDown?: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | string[], onSuccess: () => void, onError?: (message?: string) => void) => void
  type: ViewTypes
  column: ITableViewColumn | null
  colourSettings: ITableViewColour[]
  rowData?: {
    [columnId: string]: ICellValue
  }
  columnValuesCount: IColumnValuesCount
  row: ITableRow | null
  rowLevelColour?: IRowLevelColour | null
}

const styles = cellStyles()

const SELECT_CELL_MENU_WIDTH = 500
const VIEWPORT_GAP = 8

/**
 * Where a select cell's option list starts, from the cell's box: under the cell, from its left
 * edge. Where the list wouldn't fit to the right of that, it lines up with the cell's right edge
 * instead, as a popover does, rather than being pushed back into the window until it fits, which
 * left it well to the left of its cell and flush against the window's edge.
 */
export const selectCellMenuLeft = (cell: DOMRect, viewportWidth: number = window.innerWidth): number => {
  if (cell.left + SELECT_CELL_MENU_WIDTH <= viewportWidth - VIEWPORT_GAP) return cell.left
  return Math.max(VIEWPORT_GAP, cell.right - SELECT_CELL_MENU_WIDTH)
}

const SelectCellComponent: React.FC<SelectCellProps> = ({
  value,
  align,
  color,
  kind,
  readOnly,
  rowHeight,
  selected,
  isExpanded,
  isAdmin,
  isContributor,
  isJoined,
  editing,
  formValues,
  validation,
  handleKeyDown,
  setCellValue,
  column,
  type,
  colourSettings,
  rowData,
  columnValuesCount,
  row,
  onCloseSelect,
  rowLevelColour
}) => {
  const themed = useThemedColours()
  const inputRef = useRef<HTMLDivElement>(null)
  const selectionTimeout = useRef<TimeoutID | null>(null)
  const pendingCreatedValueRef = useRef<string | null>(null)
  const [selectCellMenu, setSelectCellMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const dataContext = useContext(DataContext)
  const projectContext = useContext(ProjectContext)
  const applicationContext = useContext(ApplicationContext)

  const kindOptions = column && column.kindOptions
  const [currentValue, setCurrentValue] = useState<string | string[]>(value != null ? value : '')

  // Inline option creation is only for manually-managed select columns (no
  // picklist source column) with the setting enabled, and only for editors in the grid
  const canCreateOption =
    !!dataContext &&
    !!projectContext &&
    type !== ViewTypes.FORM &&
    kindOptions?.expandOptionsViaCellEntry !== false &&
    !kindOptions?.tableOptions?.columnId &&
    !!column &&
    (isAdmin || isContributor)

  useEffect(() => {
    const newValue = value != null ? value : ''
    if (!checkValuesSame(newValue, currentValue, kind)) {
      setCurrentValue(newValue)
    }
  }, [value])

  const allOptions = (() => {
    let cachedOptions: string[]
    if (kindOptions?.tableOptions?.dependencies?.length) {
      const cachedOptionsDict = kindOptions?.tableOptions?.cachedOptions
        ? (kindOptions.tableOptions.cachedOptions as { [key: string]: string[] })
        : {}
      cachedOptions = resolveDependencyCachedOptions(
        cachedOptionsDict,
        kindOptions.tableOptions.dependencies,
        (columnId) => {
          if (type === ViewTypes.FORM) {
            return formValues ? formValues[columnId] : undefined
          }
          return rowData ? rowData[columnId] : undefined
        }
      )
    } else {
      cachedOptions = (kindOptions?.tableOptions?.cachedOptions as string[]) || []
    }

    const manualOptions = kindOptions?.manualOptions || []
    return [...cachedOptions, ...manualOptions].map((option: string) => ({ value: option, label: option }))
  })()

  const cellColours = (() => {
    if (!column) return null
    const values = Array.isArray(currentValue)
      ? (currentValue as string[])
      : currentValue
        ? [currentValue.toLocaleString()]
        : []
    const map = new Map<string, ReturnType<typeof getCellColour>>()
    for (const val of values) {
      if (!map.has(val)) {
        map.set(
          val,
          getCellColour(column, columnValuesCount, row, val, colourSettings, true, undefined, rowLevelColour, rowData)
        )
      }
    }
    return map
  })()

  useEffect(() => {
    if (!selected) {
      setSelectCellMenu(INITIAL_CONTEXT_MENU_STATE)
    }
  }, [selected])

  // Open the menu when the cell transitions into editing mode (e.g. Space/Enter
  // from the cell wrapper). Anchoring off the cell-value bounding box keeps the
  // menu in the right place without dispatching a synthetic dblclick.
  // Functional update + early-return-if-already-open so we never re-overwrite
  // a menu the dropdown button just opened.
  useEffect(() => {
    if (editing && !readOnly && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setSelectCellMenu((prev) => {
        if (prev.open) return prev
        return {
          ...prev,
          open: true,
          top: `${rect.bottom}px`,
          left: `${selectCellMenuLeft(rect)}px`,
          bottom: 'auto',
          right: 'auto'
        }
      })
    }
  }, [editing])

  const handleSetCellValue = (newValue: string | string[]) => {
    if (setCellValue && (isAdmin || isContributor)) {
      // Capture the last-committed value so we can roll back the optimistic
      // update if the save is rejected.
      const previousValue = value != null ? value : ''
      setCurrentValue(newValue)
      if (selectionTimeout.current !== null) cancelTimeout(selectionTimeout.current)
      selectionTimeout.current = requestTimeout(
        () => {
          setCellValue(
            newValue,
            () => {
              return null
            },
            () => {
              // Validation rejected the change (e.g. a no-duplicates column with hard
              // validation). Rollback the optimistic update to the last-committed value.
              setCurrentValue(previousValue)
            }
          )
        },
        kind === 'multiselect' ? 1000 : 250
      )
    }
  }

  const handleOptionSelected = (selectedOption: string) => {
    if (kind === 'select') {
      // if selected option is the same as the current value of the cell we need to clear the cell
      const newValue = selectedOption === currentValue ? '' : selectedOption
      handleSetCellValue(newValue)
      if (onCloseSelect) onCloseSelect()
    } else if (kind === 'multiselect') {
      const arrSelectedValues = currentValue ? [...(currentValue as string[])] : []

      // Remove any invalid values
      const cleanedSelectedValues = arrSelectedValues.filter(
        (selectedValue) => !!allOptions.find((option) => selectedValue === option.value)
      )

      // Check wheater the selected option is not part of the current array of values
      // if it is already there, we need to remove it.
      const newSelectedValues = cleanedSelectedValues.filter((val) => val !== selectedOption)

      // If the selected option was not part of the array of values we need to add it
      if (newSelectedValues.length === cleanedSelectedValues.length) {
        newSelectedValues.push(selectedOption)
      }
      handleSetCellValue(newSelectedValues)
    }
  }

  const handleCreateOption = (rawValue: string) => {
    const newValue = rawValue.trim()
    if (!newValue || !column || !dataContext || !projectContext) return

    // If the exact value already exists (case-sensitive, matching manual-option
    // behaviour where "Another Test" and "another test" are distinct), apply it
    // rather than adding a duplicate.
    const existing = allOptions.find((option) => option.value.trim() === newValue)
    if (existing) {
      handleOptionSelected(existing.value)
      return
    }

    // Mirror editcolumn's handleUpdateKindOptions: strip publicId and the
    // server-computed cachedOptions before sending, then append the new option.
    const newKindOptions = JSON.parse(JSON.stringify(column.kindOptions ?? {}))
    delete newKindOptions.publicId
    if (newKindOptions?.tableOptions) delete newKindOptions.tableOptions.cachedOptions
    newKindOptions.manualOptions = [...(column.kindOptions?.manualOptions ?? []), newValue]

    updateColumn(
      projectContext.project.publicId,
      'kindOptions',
      dataContext.spreadsheetData,
      dataContext.setSpreadsheetData,
      column.publicId,
      newKindOptions,
      () => {
        pendingCreatedValueRef.current = newValue
      },
      (error) => applicationContext?.displayErrorMessage(error)
    )
  }

  // Apply a freshly-created option to the cell once it appears in the column's
  // options, so the value validates as an allowed select value. The ref guard
  // means this only acts on the render after creation, when allOptions gains the
  // new value
  useEffect(() => {
    const pending = pendingCreatedValueRef.current
    if (pending != null && allOptions.some((option) => option.value === pending)) {
      pendingCreatedValueRef.current = null
      handleOptionSelected(pending)
    }
  }, [allOptions, handleOptionSelected])

  const isArray = Array.isArray(currentValue)
  const wrapTextAlignment = !isArray
    ? 'center'
    : (currentValue as string[]).length <= (rowHeight ? rowHeight * 3 : 1)
      ? 'center'
      : 'stretch'

  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value' })}
      data-focussed={selected || undefined}
      ref={inputRef}
      tabIndex={-1}
      style={{
        textAlign: align,
        color: color,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)',
        boxSizing: 'border-box',
        maxHeight: selected && isExpanded ? 'unset' : `${getRowHeightVariable(rowHeight) - 2}px`,
        overflow: selected && isExpanded ? 'visible' : selected ? 'auto' : 'hidden',
        position: selected && isExpanded ? 'relative' : undefined,
        zIndex: selected && isExpanded ? 2 : undefined
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        event.persist()

        if (event.key === ' ' && !selectCellMenu.open) {
          event.preventDefault()
          setSelectCellMenu({
            ...selectCellMenu,
            open: true,
            top: `${event.currentTarget.getBoundingClientRect().bottom}px`,
            left: `${selectCellMenuLeft(event.currentTarget.getBoundingClientRect())}px`,
            bottom: 'auto',
            right: 'auto'
          })
        } else {
          if (handleKeyDown) handleKeyDown(event)
        }
      }}
      onClick={(event: React.MouseEvent) => {
        if (readOnly) {
          isJoined ? event.persist() : event.stopPropagation()
        } else {
          event.persist()
        }
      }}
      onDoubleClick={(event: React.MouseEvent) => {
        if (readOnly) {
          event.stopPropagation()
          return
        }
        event.persist()
        setSelectCellMenu({
          ...selectCellMenu,
          open: true,
          top: `${event.currentTarget.getBoundingClientRect().bottom}px`,
          left: `${selectCellMenuLeft(event.currentTarget.getBoundingClientRect())}px`,
          bottom: 'auto',
          right: 'auto'
        })
      }}
    >
      <div
        data-testid="select-cell-inner"
        className="flex content-center"
        style={{
          maxHeight: selected && isExpanded ? 'unset' : `${getRowHeightVariable(rowHeight) - 3}px`,
          overflowY: selected && isExpanded ? 'visible' : selected ? 'auto' : 'hidden',
          alignItems: wrapTextAlignment,
          width: '100%'
        }}
      >
        {(selected || isExpanded) && !readOnly && (
          <Button
            className="p-0.5 mr-[3px] max-w-[27px] w-[27px] h-[30px]"
            internalType="grey"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              const rect = event.currentTarget.getBoundingClientRect()
              // Lined up with the cell, not just this button, when it runs out of room.
              const cell = inputRef.current?.getBoundingClientRect() ?? rect
              setSelectCellMenu((prev) => ({
                ...prev,
                open: true,
                top: `${rect.bottom}px`,
                left: `${selectCellMenuLeft(cell)}px`,
                bottom: 'auto',
                right: 'auto'
              }))
            }}
          >
            <Triangle type="down" className="h-3 w-3" />
          </Button>
        )}
        {currentValue == null || currentValue === '' || (isArray && (currentValue as string[]).length === 0) ? (
          <span>&nbsp;</span>
        ) : kind === 'select' ? (
          <span
            id="select-value-list"
            className={styles.content({
              validation,
              class: 'flex flex-wrap p-0'
            })}
          >
            {(() => {
              const cellColour = cellColours?.get(currentValue.toLocaleString()) ?? null
              return (
                <span
                  id="select-value-item"
                  data-testid="select-value-item"
                  className="whitespace-pre-line bg-surface-sunken rounded h-fit border border-divider m-0.5 py-0 px-[3px]"
                  style={{
                    backgroundColor: cellColour ? themed.fill(cellColour.backgroundColour) : 'inherit',
                    color: cellColour ? themed.text(cellColour.fontColour, cellColour.backgroundColour) : 'inherit',
                    fontWeight: cellColour ? (cellColour.fontBold ? 'bold' : 'normal') : 'normal',
                    fontStyle: cellColour ? (cellColour.fontItalic ? 'italic' : 'normal') : 'normal',
                    textDecoration: cellColour ? (cellColour.fontUnderline ? 'underline' : 'none') : 'none'
                  }}
                >
                  {currentValue != null ? currentValue.toLocaleString() : ''}
                </span>
              )
            })()}
          </span>
        ) : (
          isArray && (
            <div
              id="select-value-list"
              className={styles.content({
                validation,
                class: 'flex flex-wrap p-0'
              })}
              style={{
                height:
                  selected && isExpanded
                    ? 'auto'
                    : (currentValue as string[]).length <= (rowHeight ? rowHeight * 3 : 1)
                      ? 'fit-content'
                      : `${getRowHeightVariable(rowHeight) - 3}px`
              }}
            >
              {(currentValue as string[]).map((val, index) => {
                const cellColour = cellColours?.get(val) ?? null
                return (
                  <div
                    key={`${index}-${val}`}
                    id="select-value-item"
                    data-testid="select-value-item"
                    className="whitespace-pre-line bg-surface-sunken rounded h-fit border border-divider m-0.5 py-0 px-[3px]"
                    style={{
                      backgroundColor: cellColour ? themed.fill(cellColour.backgroundColour) : 'inherit',
                      color: cellColour ? themed.text(cellColour.fontColour, cellColour.backgroundColour) : 'inherit',
                      fontWeight: cellColour ? (cellColour.fontBold ? 'bold' : 'normal') : 'normal',
                      fontStyle: cellColour ? (cellColour.fontItalic ? 'italic' : 'normal') : 'normal',
                      textDecoration: cellColour ? (cellColour.fontUnderline ? 'underline' : 'none') : 'none'
                    }}
                  >
                    {val != null ? val.toLocaleString() : ''}
                  </div>
                )
              })}
            </div>
          )
        )}
      </div>

      {selectCellMenu.open && (
        <SelectMenu
          id="select-cell-menu"
          menuState={selectCellMenu}
          setMenuState={setSelectCellMenu}
          options={allOptions}
          onOptionClick={(option) => handleOptionSelected(option)}
          width={SELECT_CELL_MENU_WIDTH}
          multiselect={kind === 'multiselect'}
          // A single select's value is one option, not a list: wrapped, so its option shows as selected.
          optionsSelected={Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue as string] : []}
          onCreateOption={canCreateOption ? handleCreateOption : undefined}
          noOptionsMessage={
            <div className="flex flex-col gap-[5px]">
              <span>No options available</span>
              {kindOptions && kindOptions.tableOptions && kindOptions.tableOptions.columnId ? (
                <span>⚠️ Check if linked table has data</span>
              ) : (
                <span>⚠️ Please add manual options</span>
              )}
            </div>
          }
        />
      )}
    </div>
  )
}

export const SelectCell = SelectCellComponent
